/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Form, Input, Select, Button, Col, Row, Space, Switch, Collapse } from 'antd';

import { InfoTooltip } from '@components/commons/tooltip/infoTooltip';
import { addContent, clearEditions, clearContent } from '@store/reducers/intents';
import intentActions from '@store/actions/intents';
import { history } from '@utils/history';
import { MESSAGES } from '@translations/labels';
import { capitalize, toSnakeCase } from '@utils/format/text';
import { isEmpty } from '@utils/validation/isEmpty';
import { sizes } from '@styles/theme';
import domainActions from '@store/actions/domains';
import { useAgentId } from '@hooks/useAgentId';
import { useIntentId } from '@hooks/useIntentId';
import { useQuery } from '@utils/locationHelper.js';
import { ROUTES, idField, PRIORITIES } from '@constants';
import { COMMON_NAME_VALIDATION_SETTINGS } from '@common-settings';

import { IntentNewContent } from './intentContent';
import { useIntentQuality } from '../../hooks/useIntentQuality';
import { useAddIntent } from './useAddIntent';
import { useUpdateIntent } from './useUpdateIntent';
import IntentStyles from './intentFormStyles';

const VOLUMETRY_THRESHOLD = 0.8;
const SIMILARITY_THRESHOLD = 0.2;

const labels = { ...MESSAGES.manageBot, ...MESSAGES.commons };

const IntentForm = ({ editMode, readOnly }) => {
  const [form] = Form.useForm();
  const { Option } = Select;
  const { Panel } = Collapse;
  const dispatch = useDispatch();
  const { agentId } = useAgentId();
  const useIntentResponse = useIntentId();
  const domains = useSelector(state => state.domains.all);
  const params = useParams();

  const locale = useQuery().get('locale');
  let intentId;
  let incomingIntentName;

  if (params.intentId) {
    intentId = params.intentId;
  } else if (params.intentName && locale) {
    incomingIntentName = params.intentName;
  } else {
    intentId = useIntentResponse.intentId;
  }
  useEffect(() => {
    if (!isEmpty(agentId)) {
      if (!isEmpty(intentId)) {
        dispatch(intentActions.getIntent(agentId, intentId));
      }
      if (!isEmpty(incomingIntentName)) {
        dispatch(intentActions.getIntentByName(agentId, incomingIntentName, locale));
      }
      if (isEmpty(domains)) {
        // TODO: add version here
        dispatch(domainActions.getDomains(agentId));
      }
    }
  }, [agentId]);

  const { sentence, test, answer, tags } = useSelector(state => state.intents.content.toEdit);
  const { sentences, tests, answers } = useSelector(state => state.intents.content);
  const { description } = useSelector(state => state.intents.current);
  const { qualityBundle } = useIntentQuality();
  const collectionTags = useSelector(state => state.intents.content.tags);
  const { updateIntent } = useUpdateIntent();
  const { addIntent } = useAddIntent();
  const currentIntent = useSelector(state => state.intents.current);

  if (currentIntent && !intentId) {
    intentId = currentIntent[idField];
  }
  //TODO: WIP , tags for bot answers & QA
  // const tagsDB = ['type-a', 'type-b', 'type-c'].map((text) =>
  //   text.toLowerCase()
  // );
  const inputSentenceRef = useRef(null);
  const inputTestRef = useRef(null);
  const inputAnswerRef = useRef(null);
  const addAnswerButtonRef = useRef(null);

  const [showSentences, setShowSentences] = useState(false);
  const [showTests, setShowTests] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [domainId, setDomainId] = useState('');
  const [domainLanguage, setDomainLanguage] = useState('');
  const [intentName, setIntentName] = useState('');
  const [showSuggestions, setSuggestionsVisibility] = useState(false);
  const [descriptionPreview, setPreview] = useState('');
  const hasTests = tests.length > 0;
  // const hasAnswers = answers.length > 0;

  const formatSuggestions = rawInput => {
    const suggestions = [];

    if (rawInput) {
      if (rawInput.volumetryScore < VOLUMETRY_THRESHOLD) {
        const { currentLength, min, max } = rawInput.volumetries;
        if (currentLength < min) {
          suggestions.push(
            <>
              Add more sentences. The intent has <span className="highlight-number">{currentLength}</span> out of the{' '}
              {min} min recommended.
            </>
          );
        } else if (currentLength > max) {
          suggestions.push(
            <>
              Remove sentences. The intent has <span className="highlight-number">{currentLength}</span> above the {max}{' '}
              as max recommended.
            </>
          );
        }
      }

      if (rawInput.similarityScore > SIMILARITY_THRESHOLD) {
        suggestions.push(labels.suggestionSimilarity);
      }
    }

    return suggestions;
  };

  const qualitySuggestions = formatSuggestions(qualityBundle);

  const handleFinish = values => {
    const intentFields = {
      name: values.name,
      domainId: values.domain,
      isEnabled: values.isEnabled,
      isFeedbackNeeded: values.isFeedbackNeeded,
      negativeFeedback: values.negativeFeedback,
      priority: values.priority,
      description: values.description,
      utterances: sentences,
      tests,
      answers,
      conditions: collectionTags,
    };
    console.log('## handleFinish', intentFields);
    return editMode ? updateIntent(intentFields, intentId) : addIntent(intentFields);
  };

  const onReset = () => {
    dispatch(clearContent());
    form.resetFields();
  };

  const getValue = field => form.getFieldValue(field);
  const cleanValue = field => form.setFieldsValue({ [field]: field === 'selectedTags' ? [] : '' });
  const setValue = (field, text) => form.setFieldsValue({ [field]: text });

  const isDuplicated = (value, elements) => !!elements.find(element => element === value);

  useEffect(() => {
    history.listen(() => {
      onReset();
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(sentence)) setValue('sentence', sentence);
    if (!isEmpty(test)) setValue('test', test);
    if (!isEmpty(answer)) setValue('answer', answer);
    if (!isEmpty(tags)) setValue('selectedTags', tags);
    if (!isEmpty(description)) setValue('description', description);
    dispatch(clearEditions());
  }, [sentence, test, answer, tags, description]);

  const handleAddSentences = () => {
    const sentenceValue = getValue('sentence');
    if (sentenceValue && sentenceValue.length > 0 && !isDuplicated(sentenceValue, sentences)) {
      dispatch(addContent({ content: sentenceValue, contentType: 'sentences' }));
      dispatch(intentActions.computeQuality([...sentences, sentenceValue], domainLanguage));
      cleanValue('sentence');
      setShowSentences(true);
    }
    if (isDuplicated(sentenceValue, sentences)) {
      inputSentenceRef.current.focus();
    }
  };
  const handleAddTests = () => {
    const testValue = getValue('test');
    if (testValue && testValue.length > 0 && !isDuplicated(testValue, tests)) {
      dispatch(addContent({ content: testValue, contentType: 'tests' }));
      cleanValue('test');
      setShowTests(true);
    }
    if (isDuplicated(testValue, tests)) {
      inputTestRef.current.focus();
    }
  };

  const handleAddAnswers = () => {
    const answerValue = getValue('answer');
    const selectedTags = getValue('selectedTags');
    if (answerValue && answerValue.length > 0 && !isDuplicated(answerValue, answers)) {
      dispatch(addContent({ content: answerValue, contentType: 'answers' }));
      dispatch(addContent({ content: selectedTags, contentType: 'tags' }));
      cleanValue('answer');
      cleanValue('selectedTags');
      setShowAnswers(true);
    }
    if (isDuplicated(answerValue, answers)) {
      inputAnswerRef.current.focus();
    }
  };

  // const toAddAnswerButton = () => addAnswerButtonRef.current.focus();

  const getSelectedDomain = domainID => domains.filter(domain => domain[idField] === domainID);

  const getDomainLanguage = domainID => {
    const selectedDomain = getSelectedDomain(domainID);
    return selectedDomain.length === 1 ? selectedDomain[0].language : '';
  };

  const getDomainName = domainID => {
    const selectedDomain = getSelectedDomain(domainID);
    return selectedDomain.length === 1 ? selectedDomain[0].tag : '';
  };

  const onChangeDomain = value => {
    const language = getDomainLanguage(value);
    setDomainId(value);
    setDomainLanguage(language);
    if (intentName) {
      const domain = getDomainName(value);
      const newIntentName = intentName.split('.').slice(2).join('.');
      setIntentName(`${domain}.${language}.${newIntentName}`);
    }
  };

  const handleIntentName = event => {
    const intentNameTemp = event.target.value;
    if (domainLanguage.length > 1 && intentNameTemp) {
      const domain = getDomainName(domainId);
      setIntentName(`${domain}.${domainLanguage}.${intentNameTemp}`);
    }
  };

  const handleIntentDescription = event => {
    const intentDescription = event.target.value;
    setValue('description', intentDescription);

    console.log('## getValue', getValue('description'));
    setPreview(intentDescription);
  };

  if (editMode && currentIntent.domainId && domainId === '') {
    const domain = getDomainName(currentIntent.domainId);
    const language = getDomainLanguage(currentIntent.domainId);
    setIntentName(`${domain}.${language}.${currentIntent.name}`);
    onChangeDomain(currentIntent.domainId);
    setPreview(description);
    dispatch(clearContent());
    currentIntent.utterances.forEach(utterance =>
      dispatch(addContent({ content: utterance, contentType: 'sentences' }))
    );
    if (currentIntent.tests) {
      currentIntent.tests.forEach(intentTest => dispatch(addContent({ content: intentTest, contentType: 'tests' })));
    }
    currentIntent.answers.forEach(intentAnswer =>
      dispatch(addContent({ content: intentAnswer, contentType: 'answers' }))
    );
    currentIntent.conditions.forEach(condition => dispatch(addContent({ content: condition, contentType: 'tags' })));
    dispatch(intentActions.computeQuality(currentIntent.utterances, language));
    setShowSentences(true);
    setShowTests(true);
    setShowAnswers(true);
  }

  let intentContent;
  useEffect(() => {
    if (editMode && currentIntent && domains) {
      intentContent = {
        ...currentIntent,
        domain: currentIntent.domainId,
      };
    } else {
      intentContent = {
        priority: PRIORITIES[1].value,
        isEnabled: true,
        isFeedbackNeeded: false,
        negativeFeedback: false,
        sentence,
        test,
        answer,
        selectedTags: tags,
      };
    }
    form.setFieldsValue(intentContent);
  }, []);

  const labelWithTooltip = (label, tooltipMessage) => {
    return (
      <span>
        {label}
        <InfoTooltip label={tooltipMessage} />
      </span>
    );
  };

  return (
    <Col span={18}>
      <Form
        name={editMode ? 'edit-intent' : 'new-intent'}
        layout="vertical"
        form={form}
        initialValues={intentContent}
        onFinish={handleFinish}
        hideRequiredMark
        scrollToFirstError
      >
        <div
          className="relative"
          style={{
            marginBottom: sizes.giga,
          }}
        >
          <Row justify="start">
            <Col
              flex={2}
              style={{
                maxWidth: '252px',
              }}
            >
              {/* <Form.Item> */}
              <Form.Item
                name="domain"
                label={labels.formSelectDomain}
                rules={[
                  {
                    required: true,
                    message: `${labels.formSelectDomain} ${labels.fieldRequired}`,
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  autoFocus
                  allowClear
                  placeholder={labels.formSelectDomainPlaceholder}
                  disabled={readOnly}
                  onChange={onChangeDomain}
                >
                  {domains.map((domain, index) => (
                    <Option key={index} value={domain[idField]}>
                      {capitalize(domain.name)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {intentName && (
                <p
                  className="composed-intent-name absolute z-10"
                  style={{
                    marginTop: `-${sizes.kilo}`,
                    fontSize: sizes.deci,
                  }}
                >
                  {labels.formIntentName} <em>{toSnakeCase(intentName)}</em>
                </p>
              )}
              {/* </Form.Item> */}
            </Col>
            <span
              style={{
                alignSelf: 'center',
                padding: `0 ${sizes.hecto}`,
                minWidth: '65px',
              }}
            >
              {domainLanguage ? `.${domainLanguage}.` : ''}
            </span>

            <Col flex={2}>
              <Form.Item
                name="name"
                label={
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>{labels.formInputIntent}</div>
                    <div style={{ marginLeft: '305px' }}>
                      {(getValue('name') ? getValue('name').length : 0) + '/46'}
                    </div>
                  </div>
                }
                rules={[
                  {
                    required: true,
                    message: `${labels.formInputIntent} ${labels.fieldRequired}`,
                  },
                  ...COMMON_NAME_VALIDATION_SETTINGS,
                ]}
              >
                <Input
                  style={{ width: 420 }}
                  disabled={readOnly}
                  maxLength={46}
                  placeholder={labels.formInputIntentPlaceholder}
                  onChange={handleIntentName}
                  autoComplete="off"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Row justify="start">
          <Form.Item
            name="isEnabled"
            label={labelWithTooltip(labels.formSwithEnableIntent, labels.formSwithEnableIntentTooltip)}
            valuePropName="checked"
            style={{ marginRight: '2em' }}
          >
            <Switch
              unCheckedChildren="Disabled"
              disabled={readOnly}
              checkedChildren="Enabled"
              defaultChecked={editMode && currentIntent ? currentIntent.isEnabled : true}
            />
          </Form.Item>
          {/*// TODO: WIP , isFeedback Needed */}
          {/* <Form.Item
            name="isFeedbackNeeded"
            label={labelWithTooltip(labels.formSwithAskFeedback, labels.formSwithAskFeedbackTooltip)}
            valuePropName="checked"
          >
            <Switch
              disabled={readOnly}
              defaultChecked={
                editMode && intent ? intent.isFeedbackNeeded : false
              }
            />
          </Form.Item> */}
          <Form.Item
            name="negativeFeedback"
            label={labelWithTooltip(labels.formSwithNegativeFeedback, labels.formSwithNegativeFeedbackTooltip)}
            valuePropName="checked"
          >
            <Switch
              disabled={readOnly}
              defaultChecked={editMode && currentIntent ? currentIntent.negativeFeedback : false}
            />
          </Form.Item>
        </Row>
        <Col span={11}>
          <Form.Item
            name="priority"
            label={labelWithTooltip(labels.formSelectPriority, labels.formSelectPriorityTooltip)}
            rules={[
              {
                required: true,
                message: `${labels.formSelectPriority} ${labels.fieldRequired}`,
              },
            ]}
          >
            <Select disabled={readOnly} placeholder={labels.formSelectPriorityPlaceholder} allowClear>
              {PRIORITIES.map(priority => (
                <Option key={priority.name} value={priority.value}>
                  {capitalize(priority.name)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Form.Item
          name="description"
          label={labelWithTooltip(labels.formInputDescription, labels.formInputDescriptionTooltip)}
        >
          <Input
            disabled={readOnly}
            placeholder={labels.formInputDescriptionPlaceholder}
            defaultValue={description}
            onChange={handleIntentDescription}
            autoComplete="off"
          />
          {!!descriptionPreview && (
            <div>
              <b>Question:</b>
              {` Do you mean you want to ${descriptionPreview}?`}
            </div>
          )}
          {!descriptionPreview && (
            <div>
              <b>Question:</b>
              {` Do you mean you want to...?`}
            </div>
          )}
        </Form.Item>
        <h4 className="section-title-input-form">{labels.formIntentSectionTitle}</h4>
        {!readOnly && (
          <Row align="middle" justify="space-between">
            <Col span={20}>
              <Form.Item
                name="sentence"
                className="custom-required-input"
                label={labels.formUserSentences}
                rules={[
                  {
                    validator: () => {
                      if (sentences.length === 0) {
                        return Promise.reject('You should save at least an example');
                      } else {
                        return Promise.resolve();
                      }
                    },
                  },
                ]}
              >
                <Input
                  disabled={readOnly}
                  ref={inputSentenceRef}
                  placeholder={labels.formUserSentencesPlaceholder}
                  onPressEnter={handleAddSentences}
                />
              </Form.Item>
            </Col>
            <Col span={2.5}>
              <Button
                disabled={readOnly}
                type="primary"
                htmlType="button"
                className="uppercase"
                onClick={handleAddSentences}
              >
                {labels.btnAdd}
              </Button>
            </Col>
          </Row>
        )}

        {/* QUALITY SUGGESTIONS */}

        {!!qualitySuggestions && !!qualitySuggestions.length && (
          <Row className="quality">
            Intent quality:
            <span className="quality-score">{(!!qualityBundle && qualityBundle.quality.toFixed(2)) || 0}</span> / 10
            <span
              className="quality-details"
              onClick={() => setSuggestionsVisibility(!showSuggestions)}
              onKeyPress={() => setSuggestionsVisibility(!showSuggestions)}
              role="button"
              tabIndex="0"
            >
              ({showSuggestions ? 'hide' : 'show'} detail)
            </span>
            <InfoTooltip label="Number of sentences used to train the bot. A minimum of 10 sentences is recommended. " />
          </Row>
        )}

        {!!showSuggestions && (
          <Row className="quality-suggestions">
            <ul className="reset-list">
              {!!qualitySuggestions.length &&
                qualitySuggestions.map(suggestion => <li key={suggestion}>{suggestion}</li>)}
            </ul>
          </Row>
        )}

        {/* ___________ */}

        {!!showSentences && (
          <IntentNewContent
            readOnly={readOnly}
            inputName="sentence"
            inputReference={inputSentenceRef.current}
            domainLanguage={domainLanguage}
          />
        )}
        {/*// TODO: WIP , tags for bot answers & QA */}
        {
          <Collapse className="qa-section" defaultActiveKey={hasTests && '1'}>
            <Panel header="QA" key="1">
              <Row align="middle" justify="space-between">
                <Col span={20}>
                  <Form.Item
                    name="test"
                    label={labelWithTooltip(labels.formUserTests, labels.formUserTestsTooltip)}
                    rules={[
                      {
                        required: !hasTests,
                        message: `${labels.formUserSentences} ${labels.fieldRequired}`,
                      },
                    ]}
                  >
                    <Input
                      disabled={readOnly}
                      ref={inputTestRef}
                      placeholder={labels.formUserSentencesPlaceholder}
                      onPressEnter={handleAddTests}
                    />
                  </Form.Item>
                </Col>
                <Col span={2.5}>
                  <Button
                    disabled={readOnly}
                    type="primary"
                    htmlType="button"
                    className="uppercase"
                    onClick={handleAddTests}
                  >
                    {labels.btnAdd}
                  </Button>
                </Col>
              </Row>
              {!!showTests && (
                <IntentNewContent
                  inputName="test"
                  inputReference={inputTestRef.current}
                  domainLanguage={domainLanguage}
                />
              )}
            </Panel>
          </Collapse>
        }

        {!readOnly && (
          <>
            <Row align="bottom" justify="space-between">
              <Col span={20}>
                <Form.Item
                  name="answer"
                  label={labels.formBotAnswers}
                  className="margin-bottom-small custom-required-input"
                  rules={[
                    {
                      validator: () => {
                        if (answers.length === 0) {
                          return Promise.reject('You should save at least an answer');
                        } else {
                          return Promise.resolve();
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    ref={inputAnswerRef}
                    placeholder={labels.formBotAnswersPlaceholder}
                    onPressEnter={handleAddAnswers}
                  />
                </Form.Item>
              </Col>

              <Col span={2.5}>
                <Button
                  type="primary"
                  htmlType="button"
                  ref={addAnswerButtonRef}
                  className="uppercase margin-bottom-small"
                  onClick={handleAddAnswers}
                >
                  {labels.btnAdd}
                </Button>
              </Col>
            </Row>
          </>
        )}

        {/* <Col span={20}>
          <Form.Item name="selectedTags">
            <Select
              mode="multiple"
              disabled={readOnly}
              allowClear
              tokenSeparators={[',']}
              placeholder={labels.formBotAnswersTagsPlaceholder}
              onBlur={toAddAnswerButton}
            >
              {tagsDB.map((tag) => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col> 
      */}

        {showAnswers && (
          <IntentNewContent readOnly={readOnly} inputName="answer" inputReference={inputAnswerRef.current} />
        )}
        <Form.Item className="text-right">
          <Space size="middle">
            <Button type="link" htmlType="button" onClick={onReset} className="black uppercase">
              <Link to={ROUTES.manageBot.intents.path} className="link-secondary block">
                {labels.btnCancel}
              </Link>
            </Button>
            <Button disabled={readOnly} type="primary" htmlType="submit" className="uppercase">
              {editMode ? labels.btnEditIntent : labels.btnCreateIntent}
            </Button>
          </Space>
        </Form.Item>
      </Form>
      <style jsx>{IntentStyles}</style>
    </Col>
  );
};

IntentForm.propTypes = {
  editMode: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export { IntentForm };
